import { api } from "./api";
import {
  buildPagePath,
  renderHeaders,
  renderPage,
  renderPwaManifest,
  renderRedirects,
  renderRobots,
  renderServiceWorker,
  renderSitemap,
  type PageDoc,
  type Site,
  type Theme,
} from "./render";
// @ts-ignore - vite ?raw import
import neueraCss from "../neuera-assets/neuera.css?raw";
// @ts-ignore - vite ?raw import
import neueraJs from "../neuera-assets/neuera.js?raw";
// @ts-ignore - vite ?raw import
import gridShimCss from "../neuera-assets/grid-shim.css?raw";

type Snapshot = {
  site: Site;
  theme: Theme | null;
  variables: Record<string, string>;
  pages: PageDoc[];
  redirects: Array<{ source: string; target: string; status: number }>;
};

type Settings = {
  pwaEnabled?: boolean;
  metaTitleTemplate?: string;
  metaDescriptionFallback?: string;
  cspOverrides?: { scriptSrc?: string[]; styleSrc?: string[]; connectSrc?: string[]; imgSrc?: string[]; frameSrc?: string[] };
  gtmContainerId?: string;
};

export type BuildFile = { path: string; content_b64: string; content_type?: string };

export async function runPublish(siteSlug: string, onLog: (line: string) => void): Promise<{ deployment_id: string; url: string }> {
  onLog("Starting build…");
  const create = await api.post<{ build_id: string }>(`/api/sites/${siteSlug}/builds`);
  const buildId = create.build_id;
  onLog(`Build ${buildId} created.`);

  onLog("Fetching snapshot…");
  const [snap, settings] = await Promise.all([
    api.get<Snapshot>(`/api/sites/${siteSlug}/builds/${buildId}/snapshot`),
    api.get<Settings>(`/api/sites/${siteSlug}/settings`).catch(() => ({} as Settings)),
  ]);
  onLog(`Snapshot loaded — ${snap.pages.length} page(s), ${Object.keys(snap.variables).length} variable(s).`);

  const apiBase = import.meta.env.VITE_API_BASE ?? "";
  const assetUrl = (id: string) => `${apiBase}/api/sites/${siteSlug}/assets/${id}/raw`;
  const origin = snap.site.primary_domain ? `https://${snap.site.primary_domain}` : `https://${snap.site.slug}.pages.dev`;

  const files: BuildFile[] = [];

  for (const page of snap.pages) {
    if (!page.revision) continue;
    if (page.revision.metadata.behavior?.excludeFromBuild) continue;
    // Patch _siteSlug into form blocks so the rendered form posts to the right relay path
    page.revision.blocks.forEach((b) => {
      if (b.componentKey === "form.builder") (b.props as Record<string, unknown>)._siteSlug = siteSlug;
    });
    const out = renderPage({
      site: snap.site,
      page,
      allPages: snap.pages,
      theme: snap.theme,
      variables: snap.variables,
      assetUrl,
      settings,
      origin,
    });
    if (!out) continue;
    files.push({ path: out.path, content_b64: encodeB64(out.html), content_type: "text/html; charset=utf-8" });
    onLog(`Rendered ${out.path}`);
    void buildPagePath; // referenced by renderPage
  }

  // Site assets (neuera CSS + JS) bundled into each published site
  files.push({ path: "/assets/neuera.css", content_b64: encodeB64(String(neueraCss)), content_type: "text/css; charset=utf-8" });
  files.push({ path: "/assets/grid-shim.css", content_b64: encodeB64(String(gridShimCss)), content_type: "text/css; charset=utf-8" });
  files.push({ path: "/assets/neuera.js", content_b64: encodeB64(String(neueraJs)), content_type: "application/javascript; charset=utf-8" });

  // Sitemap + robots + headers + redirects
  files.push({ path: "/sitemap.xml", content_b64: encodeB64(renderSitemap(snap.site, snap.pages, origin)), content_type: "application/xml" });
  files.push({ path: "/robots.txt", content_b64: encodeB64(renderRobots(origin)), content_type: "text/plain" });
  files.push({ path: "/_headers", content_b64: encodeB64(renderHeaders(settings)), content_type: "text/plain" });
  const redirects = renderRedirects(snap.redirects);
  if (redirects) files.push({ path: "/_redirects", content_b64: encodeB64(redirects), content_type: "text/plain" });

  if (settings.pwaEnabled) {
    files.push({ path: "/manifest.webmanifest", content_b64: encodeB64(renderPwaManifest(snap.site, snap.theme)), content_type: "application/manifest+json" });
    files.push({ path: "/sw.js", content_b64: encodeB64(renderServiceWorker()), content_type: "application/javascript" });
    onLog("PWA manifest + service worker emitted.");
  }

  onLog(`Uploading ${files.length} file(s) to Pages…`);
  const res = await api.post<{ ok: boolean; deployment_id: string; url: string }>(
    `/api/sites/${siteSlug}/builds/${buildId}/finalize`,
    { files },
  );
  onLog(`Deployment ${res.deployment_id} — ${res.url}`);
  return res;
}

function encodeB64(s: string): string {
  // Browser-side base64 with proper UTF-8 handling
  const bytes = new TextEncoder().encode(s);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  return btoa(bin);
}
